<template>
  <main-template>
    <v-row>
      <v-col>
        <h1>
          <v-btn
            id="btn-voltar-series"
            :disabled="submittingForm"
            color="primary"
            text
            x-small
            @click="goBackToSeries"
          >
            <v-icon left>fa fa-angle-left</v-icon>
          </v-btn>
          {{ pageTitle }}
        </h1>
      </v-col>
    </v-row>

    <ValidationObserver v-slot="{ handleSubmit }">
      <form :disabled="submittingForm" @submit.prevent="handleSubmit(submitForm)">
        <v-row>
          <v-col cols="12">
            <v-card class="mb-4 pa-4" elevation="1">
              <v-card-title class="font-weight-bold mb-4 pa-0 text-subtitle-1">
                Informações Básicas:
              </v-card-title>

              <v-row>
                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Descrição" rules="required" v-slot="{ errors }">
                    <e-label class="required">Descrição</e-label>
                    <v-text-field
                      id="input-descricao"
                      v-model="form.descricao"
                      dense
                      disabled
                      :error-messages="errors"
                      hide-details="auto"
                      outlined
                      placeholder="Digite a descrição da etapa"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="Curso" rules="required" v-slot="{ errors }">
                    <e-label class="required">Curso</e-label>
                    <e-autocomplete
                      id="autocomplete-curso"
                      v-model="form.curso_id"
                      dense
                      disabled
                      :error-messages="errors"
                      hide-details="auto"
                      :items="sortedCursosList"
                      :item-text="cursoText"
                      :item-value="cursoValue"
                      :loading="isLoadingCursos"
                      outlined
                      placeholder="Selecione um curso"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" md="6" lg="4">
                  <ValidationProvider name="historico" v-slot="{ errors }">
                    <div class="align-center d-flex mt-6">
                      <v-checkbox
                        id="checkbox-historico"
                        v-model="form.historico"
                        class="mt-0"
                        dense
                        hide-details
                        label="Histórico?"
                      />
                      <v-tooltip max-width="310" top>
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon
                            id="icon-info-historico"
                            v-bind="attrs"
                            v-on="on"
                            class="ml-1"
                            color="grey"
                            size="20"
                          >
                            mdi-information-outline
                          </v-icon>
                        </template>
                        <span class="text-body-1">
                          Marque esta opção para incluir esta etapa nas opções de etapa ao criar o
                          histórico escolar do aluno.
                        </span>
                      </v-tooltip>
                    </div>
                    <div v-if="errors.length" class="error--text text-caption mt-1 v-messages">
                      {{ errors[0] }}
                    </div>
                  </ValidationProvider>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12">
            <v-card class="pa-4" elevation="1">
              <v-card-title
                class="align-center d-flex font-weight-bold justify-space-between mb-4 pa-0 text-subtitle-1"
              >
                <div class="align-center d-flex">
                  Componentes Curriculares:
                  <v-chip class="ml-2" color="primary">
                    {{ selectedDisciplinesCount }} selecionadas
                  </v-chip>
                </div>
                <v-checkbox
                  id="checkbox-marcar-todos"
                  v-model="isSelectAll"
                  class="mr-5"
                  :label="selectAllLabel"
                  @change="toggleAllDisciplines"
                />
              </v-card-title>

              <e-label class="mb-3"
                >Selecione os componentes curriculares e defina a carga horária:</e-label
              >

              <v-row>
                <v-col
                  v-for="(disciplina, index) in sortedDisciplinasList"
                  :key="disciplina.id"
                  cols="12"
                  sm="6"
                  md="4"
                  lg="3"
                >
                  <v-card
                    class="mb-3 pa-3"
                    :class="{ 'selected-disciplina': isDisciplineSelected(disciplina.id) }"
                    elevation="1"
                    outlined
                  >
                    <div class="align-start d-flex">
                      <v-checkbox
                        :id="`checkbox-disciplina-${disciplina.id}`"
                        v-model="form.disciplinaIdList"
                        class="flex-grow-1 mt-0"
                        :data-testid="`disciplina-checkbox-${disciplina.id}`"
                        :disabled="shouldDisableDisciplina(disciplina.id)"
                        hide-details
                        :label="disciplina.descricao"
                        :value="disciplina.id"
                      />

                      <v-tooltip
                        v-if="shouldDisableDisciplina(disciplina.id)"
                        bottom
                        color="#000000"
                        max-width="450"
                      >
                        <template v-slot:activator="{ on, attrs }">
                          <v-icon v-bind="attrs" v-on="on" class="ml-4" color="primary" size="20">
                            mdi-information-outline
                          </v-icon>
                        </template>

                        <div style="font-size: 14px; text-align: center">
                          <template
                            v-if="
                              getQuantidadeAulas(disciplina.id) > 0 ||
                              getQuantidadeNotas(disciplina.id) > 0 ||
                              getQuantidadePlanos(disciplina.id) > 0
                            "
                          >
                            <div class="mb-2">
                              Não é possível desvincular essa disciplina pois existem registros
                              vinculados a ela nesta etapa:
                            </div>

                            <div
                              style="
                                display: flex;
                                flex-direction: column;
                                gap: 6px;
                                margin-top: 8px;
                                width: 100%;
                              "
                            >
                              <div
                                style="
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  font-weight: bold;
                                  width: 100%;
                                  text-align: center;
                                  align-items: center;
                                "
                              >
                                <span>Tipo</span>
                                <span>Quantidade</span>
                              </div>

                              <div
                                v-if="getQuantidadeAulas(disciplina.id) > 0"
                                style="
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  width: 100%;
                                  text-align: center;
                                  align-items: center;
                                "
                              >
                                <span> Aulas lançadas </span>

                                <span>
                                  {{ getQuantidadeAulas(disciplina.id) }}
                                </span>
                              </div>

                              <div
                                v-if="getQuantidadeNotas(disciplina.id) > 0"
                                style="
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  width: 100%;
                                  text-align: center;
                                  align-items: center;
                                "
                              >
                                <span> Frequências lançadas </span>

                                <span>
                                  {{ getQuantidadePresencas(disciplina.id) }}
                                </span>
                              </div>

                              <div
                                v-if="getQuantidadeNotas(disciplina.id) > 0"
                                style="
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  width: 100%;
                                  text-align: center;
                                  align-items: center;
                                "
                              >
                                <span> Notas lançadas </span>

                                <span>
                                  {{ getQuantidadeNotas(disciplina.id) }}
                                </span>
                              </div>

                              <div
                                v-if="getQuantidadePlanos(disciplina.id) > 0"
                                style="
                                  display: grid;
                                  grid-template-columns: 1fr 1fr;
                                  width: 100%;
                                  text-align: center;
                                  align-items: center;
                                "
                              >
                                <span> Planos cadastrados </span>

                                <span>
                                  {{ getQuantidadePlanos(disciplina.id) }}
                                </span>
                              </div>
                            </div>

                            <div class="mt-3">
                              Entre em contato com a equipe pedagógica para esclarecimentos.
                            </div>
                          </template>

                          <template v-else>
                            <div class="mb-2">
                              Não é possível desvincular essa disciplina pois ela já está vinculada
                              à gestões nesta etapa.
                            </div>

                            <div class="mt-3">
                              Entre em contato com a equipe pedagógica para esclarecimentos.
                            </div>
                          </template>
                        </div>
                      </v-tooltip>
                    </div>

                    <v-expand-transition>
                      <div v-if="isDisciplineSelected(disciplina.id)" class="mt-3">
                        <ValidationProvider
                          :name="`Carga horária - ${disciplina.descricao}`"
                          rules="required|min_value:1|max_value:999"
                          v-slot="{ errors }"
                        >
                          <e-label v-if="!disciplina.disciplina_extra_curricular" class="required">
                            Carga horária (horas)
                          </e-label>
                          <v-text-field
                            v-if="!disciplina.disciplina_extra_curricular"
                            :id="`campo-carga-horaria-${index}`"
                            v-model.number="disciplina.carga_horaria_disciplina"
                            class="carga-horaria-field"
                            dense
                            :error-messages="errors"
                            hide-details="auto"
                            max="999"
                            min="1"
                            outlined
                            placeholder="Ex: 180"
                            type="number"
                            v-mask="'###'"
                          />

                          <div class="align-center d-flex mt-2">
                            <v-switch
                              :id="`input-inep-status-${disciplina.id}`"
                              v-model="disciplina.disciplina_extra_curricular"
                              hide-details
                              label="Complementar"
                              @change="clearWorkload(disciplina)"
                            />
                            <v-tooltip max-width="425" right>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  id="icon-info-historico"
                                  v-bind="attrs"
                                  v-on="on"
                                  class="ml-4 mt-5"
                                  color="grey"
                                  size="20"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span class="text-body-1">
                                Ao ativar a opção 'Complementar' a carga horária do componente
                                curricular complementar será zero e o componente curricular não
                                aparecerá nos relatórios e dashboards.
                              </span>
                            </v-tooltip>
                          </div>

                          <div class="align-center d-flex mt-2">
                            <v-switch
                              :id="`input-permite-plano-${disciplina.id}`"
                              v-model="disciplina.permite_plano"
                              hide-details
                              label="Permite lançamento de plano"
                            />
                            <v-tooltip max-width="425" right>
                              <template v-slot:activator="{ on, attrs }">
                                <v-icon
                                  v-bind="attrs"
                                  v-on="on"
                                  class="ml-4 mt-5"
                                  color="grey"
                                  size="20"
                                >
                                  mdi-information-outline
                                </v-icon>
                              </template>
                              <span class="text-body-1">
                                Quando ativado, esta disciplina permitirá lançamento de plano
                                pedagógico.
                              </span>
                            </v-tooltip>
                          </div>
                        </ValidationProvider>
                      </div>
                    </v-expand-transition>
                  </v-card>
                </v-col>
              </v-row>

              <div v-if="isEmptyDisciplinesList" class="py-8 text-center">
                <v-icon class="mb-2" color="grey lighten-1" size="48">mdi-book-off</v-icon>
                <div class="grey--text text-body-1">Nenhuma disciplina disponível</div>
              </div>
            </v-card>
          </v-col>

          <v-col cols="12" class="mt-6">
            <v-card class="pa-4" color="grey lighten-4" elevation="0">
              <v-row>
                <v-col cols="12" md="6" class="d-flex">
                  <v-btn
                    id="btn-limpar-dados"
                    :disabled="submittingForm"
                    block
                    class="mr-md-2"
                    color="primary"
                    outlined
                    @click="clearForm"
                  >
                    <v-icon left>mdi-delete</v-icon>
                    Limpar Dados
                  </v-btn>
                  <v-btn
                    id="btn-enviar-dados"
                    :disabled="submittingForm"
                    :loading="submittingForm"
                    block
                    class="ml-md-2"
                    color="primary"
                    type="submit"
                  >
                    <v-icon left>mdi-check</v-icon>
                    {{ submitButtonLabel }}
                  </v-btn>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>
      </form>
    </ValidationObserver>
  </main-template>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import ELabel from "../../components/ELabel.vue";

export default {
  name: "YearForm",

  components: { ELabel },

  props: {
    editing: {
      default: false,
      type: Boolean,
    },
  },

  data() {
    return {
      cursosList: [],
      disciplinasComGestao: {},
      disciplinasList: [],
      form: {
        curso_id: null,
        descricao: "",
        disciplinaIdList: [],
        historico: false,
        id: null,
      },
      isSelectAll: false,
      submittingForm: false,
    };
  },

  computed: {
    ...mapGetters("Cursos", ["carregandoCursos"]),
    ...mapGetters("Series", ["series"]),

    isEmptyDisciplinesList() {
      return this.disciplinasList.length === 0;
    },

    isLoadingCursos() {
      return this.carregandoCursos;
    },

    pageTitle() {
      return this.editing ? "Editando Etapa" : "Criando Etapa";
    },

    selectAllLabel() {
      return this.isSelectAll ? "Desmarcar todos" : "Marcar todos";
    },

    selectedDisciplinesCount() {
      return this.form.disciplinaIdList.length;
    },

    sortedCursosList() {
      return [...this.cursosList].sort((a, b) =>
        (a.descricao || "").localeCompare(b.descricao || "")
      );
    },

    sortedDisciplinasList() {
      return [...this.disciplinasList].sort((a, b) =>
        (a.descricao || "").localeCompare(b.descricao || "")
      );
    },

    submitButtonLabel() {
      return this.editing ? "Atualizar Etapa" : "Criar Etapa";
    },
  },

  watch: {
    "form.disciplinaIdList": {
      deep: true,
      handler(newValue) {
        this.isSelectAll = newValue.length === this.disciplinasList.length;
      },
    },
  },

  async mounted() {
    await this.loadData();
  },

  methods: {
    ...mapActions("Cursos", ["loadCursos"]),
    ...mapActions("Series", ["loadSeries"]),
    ...mapActions("Disciplinas", ["loadDisciplinas"]),

    buildDisciplinasPayload() {
      const disciplinasPayload = {};
      this.form.disciplinaIdList.forEach((id) => {
        const disciplina = this.disciplinasList.find((d) => d.id === id);
        if (disciplina) {
          disciplinasPayload[id] = {
            carga_horaria_disciplina: disciplina.carga_horaria_disciplina || null,
            disciplina_extra_curricular: disciplina.disciplina_extra_curricular || false,
            permite_plano: disciplina.permite_plano || false,
          };
        }
      });
      return disciplinasPayload;
    },

    clearForm() {
      this.form.disciplinaIdList = [];
      this.disciplinasList.forEach((disciplina) => {
        disciplina.carga_horaria_disciplina = null;
        disciplina.disciplina_extra_curricular = false;
        disciplina.permite_plano = false;
      });
      this.isSelectAll = false;
    },

    clearWorkload(disciplina) {
      if (disciplina.disciplina_extra_curricular) {
        disciplina.carga_horaria_disciplina = null;
      }
    },

    cursoText(curso) {
      return curso.descricao;
    },

    cursoValue(curso) {
      return curso.id;
    },

    getQuantidadeAulas(disciplinaId) {
      return (this.disciplinasComGestao[disciplinaId] || []).reduce(
        (total, gestao) => total + (gestao.quantidade_aulas || 0),
        0
      );
    },

    getQuantidadePresencas(disciplinaId) {
      return (this.disciplinasComGestao[disciplinaId] || []).reduce(
        (total, gestao) => total + (gestao.quantidade_presencas || 0),
        0
      );
    },

    getQuantidadeNotas(disciplinaId) {
      return (this.disciplinasComGestao[disciplinaId] || []).reduce(
        (total, gestao) => total + (gestao.quantidade_notas || 0),
        0
      );
    },

    getQuantidadePlanos(disciplinaId) {
      return (this.disciplinasComGestao[disciplinaId] || []).reduce(
        (total, gestao) => total + (gestao.quantidade_planos || 0),
        0
      );
    },

    goBackToSeries() {
      this.$router.push({ name: "series" });
    },

    hasGestao(disciplinaId) {
      return !!this.disciplinasComGestao[disciplinaId];
    },

    hasRegistrosRelacionados(disciplinaId) {
      return (
        this.getQuantidadeAulas(disciplinaId) > 0 ||
        this.getQuantidadeNotas(disciplinaId) > 0 ||
        this.getQuantidadePresencas(disciplinaId) > 0 ||
        this.getQuantidadePlanos(disciplinaId) > 0
      );
    },

    initializeForm(serie) {
      this.form = {
        curso_id: serie.curso_id || null,
        descricao: serie.descricao || "",
        disciplinaIdList: serie?.disciplinas?.map((disciplina) => disciplina.id) || [],
        historico: serie.historico || false,
        id: serie?.id || null,
      };
    },

    isDisciplineSelected(disciplineId) {
      return this.form.disciplinaIdList.includes(disciplineId);
    },

    async loadCursosData() {
      const response = await this.$services.cursosService.syncAll();
      this.cursosList = response.map((curso) => ({
        ...curso,
        "data-testid": `curso-item-${curso.id}`,
      }));
    },

    async loadData() {
      try {
        this.$loaderService.open("Carregando dados");

        const { serie_id } = this.$route.params;
        const serie = serie_id ? await this.$services.seriesService.getSerie(serie_id) : {};
        if (serie?.id) {
          this.disciplinasComGestao = await this.$services.seriesService.getDisciplinasComGestao(
            serie.id
          );
        }
        await Promise.all([this.loadCursosData(), this.loadDisciplinasData(serie)]);

        this.initializeForm(serie);
        this.$loaderService.close();
      } catch (error) {
        this.$handleError(error);
      }
    },

    async loadDisciplinasData(serie) {
      const allDisciplinas = await this.$services.disciplinasService.syncAll();
      this.disciplinasList = allDisciplinas.map((disciplina) => {
        const associatedDisciplina = serie?.disciplinas?.find((d) => d.id === disciplina.id);
        return {
          ...disciplina,
          "data-testid": `disciplina-item-${disciplina.id}`,
          carga_horaria_disciplina: associatedDisciplina
            ? associatedDisciplina.pivot.carga_horaria_disciplina
            : null,
          disciplina_extra_curricular: associatedDisciplina
            ? !!associatedDisciplina.pivot.disciplina_extra_curricular
            : false,
          permite_plano: associatedDisciplina ? associatedDisciplina.pivot.permite_plano : false,
        };
      });
    },

    shouldDisableDisciplina(disciplinaId) {
      return this.isDisciplineSelected(disciplinaId) && this.hasRegistrosRelacionados(disciplinaId);
    },

    async submitForm() {
      this.submittingForm = true;

      const disciplinasPayload = this.buildDisciplinasPayload();
      const payload = {
        ...this.form,
        disciplinaIdList: disciplinasPayload,
      };

      try {
        if (this.editing) {
          await this.$services.seriesService.atualizarSerie(payload);
          this.$toast.success("Etapa atualizada com sucesso");
        } else {
          await this.$services.seriesService.criarSerie(payload);
          this.$toast.success("Etapa criada com sucesso");
        }
        this.$router.push({ name: "series" });
      } catch (error) {
        this.$toast.danger(error.error.message);
        setTimeout(() => {
          this.$toast.danger("Remova os registros antes de realizar a desvinculação.");
        }, 3000);
      } finally {
        this.submittingForm = false;
      }
    },

    toggleAllDisciplines() {
      if (this.isSelectAll) {
        this.form.disciplinaIdList = this.disciplinasList
          .filter((disciplina) => !this.hasGestao(disciplina.id))
          .map((disciplina) => disciplina.id);
      } else {
        this.form.disciplinaIdList = this.form.disciplinaIdList.filter((id) => this.hasGestao(id));
      }
    },
  },
};
</script>

<style scoped>
.carga-horaria-field {
  max-width: 120px;
}

.required::after {
  color: red;
  content: " *";
}

.selected-disciplina {
  background-color: #e3f2fd;
  border: 2px solid #2196f3;
}

.v-card {
  border-radius: 8px;
  transition: all 0.3s ease;
}
</style>
